import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import CONFIG from "../../config/config";
import { Card, CardBody, CardFooter, CardHeader } from "../../components/card/card";
import { getAuthHeaders } from "../../misc.js";
import genres from "../../config/genres.js"; // genres array

const createChannel = async (data) => {
  const response = await fetch(`${CONFIG.API_URL}/channel/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify(data),
  });
  return response.json();
};

const ChannelCreate = () => {
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [channelId, setChannelId] = useState("");
  const [channelType, setChannelType] = useState("");
  const [genre, setGenre] = useState(""); // New state for genre
  const [channelUsername, setChannelUsername] = useState("");

  const [logoFile, setLogoFile] = useState(null);
  const [thumbnailFiles, setThumbnailFiles] = useState([]);
  const [videoBackgroundFile, setVideoBackgroundFile] = useState(null);
  const [shortBackgroundFile, setShortBackgroundFile] = useState(null);

  const [logoPreview, setLogoPreview] = useState(null);
  const [thumbnailPreviews, setThumbnailPreviews] = useState([]);
  const [videoBackgroundPreview, setVideoBackgroundPreview] = useState(null);
  const [shortBackgroundPreview, setShortBackgroundPreview] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // List of supported languages
  const languages = [
    { code: "en", name: "English" },
    { code: "es", name: "Spanish" },
    { code: "fr", name: "French" },
    { code: "de", name: "German" },
    { code: "it", name: "Italian" },
    { code: "ja", name: "Japanese" },
    { code: "zh", name: "Chinese" },
  ];

  // List of channel types (should match your Mongoose schema enum)
  const channelTypes = [
    { value: "xvid", label: "Xvid" },
    { value: "quiz", label: "Quiz" },
    { value: "song_reel", label: "Song Reel" },
  ];

  // Clean up object URLs when component unmounts or files change
  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      thumbnailPreviews.forEach((preview) => URL.revokeObjectURL(preview));
      if (videoBackgroundPreview) URL.revokeObjectURL(videoBackgroundPreview);
      if (shortBackgroundPreview) URL.revokeObjectURL(shortBackgroundPreview);
    };
  }, [logoPreview, thumbnailPreviews, videoBackgroundPreview, shortBackgroundPreview]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    setLogoFile(file);
    if (file) {
      const previewURL = URL.createObjectURL(file);
      setLogoPreview(previewURL);
    } else {
      setLogoPreview(null);
    }
  };

  const handleThumbnailChange = (e) => {
    const files = Array.from(e.target.files);
    setThumbnailFiles(files);
    thumbnailPreviews.forEach((preview) => URL.revokeObjectURL(preview));
    const previews = files.map((file) => URL.createObjectURL(file));
    setThumbnailPreviews(previews);
  };

  const handleVideoBackgroundChange = (e) => {
    const file = e.target.files[0];
    setVideoBackgroundFile(file);
    if (file) {
      const previewURL = URL.createObjectURL(file);
      setVideoBackgroundPreview(previewURL);
    } else {
      setVideoBackgroundPreview(null);
    }
  };

  const handleShortBackgroundChange = (e) => {
    const file = e.target.files[0];
    setShortBackgroundFile(file);
    if (file) {
      const previewURL = URL.createObjectURL(file);
      setShortBackgroundPreview(previewURL);
    } else {
      setShortBackgroundPreview(null);
    }
  };

  const handleCreateChannel = async () => {
    // Ensure required fields are provided including genre
    if (!name || !language || !voiceId || !channelId || !channelType || !genre) {
      setError("All fields are required.");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setSuccess(false);

      const slug = name.toLowerCase().replace(/\s+/g, "-"); // Auto-generate slug
      const response = await createChannel({
        name,
        slug,
        language,
        voiceId,
        channelId,
        type: channelType,
        genre,
        channel_username: channelUsername
      });

      if (response.success) {
        // If logo or thumbnails are provided, upload them
        if (logoFile || thumbnailFiles.length > 0) {
          const formData = new FormData();
          if (logoFile) {
            formData.append("logo", logoFile);
          }
          thumbnailFiles.forEach((file) => {
            formData.append("thumbnails", file);
          });
          const uploadResponse = await fetch(
            `${CONFIG.API_URL}/channel/${response.data._id}/upload-images`,
            {
              method: "POST",
              headers: {
                ...getAuthHeaders(),
              },
              body: formData,
            }
          );
          if (!uploadResponse.ok) {
            console.error("Failed to upload images");
          }
        }

        // If short background is provided, upload it
        if (shortBackgroundFile) {
          const shortBgFormData = new FormData();
          shortBgFormData.append("short_background", shortBackgroundFile);
          const shortBgUploadResponse = await fetch(
            `${CONFIG.API_URL}/channel/${response.data._id}/upload-short-background`,
            {
              method: "POST",
              headers: {
                ...getAuthHeaders(),
              },
              body: shortBgFormData,
            }
          );
          if (!shortBgUploadResponse.ok) {
            console.error("Failed to upload short background");
          }
        }

        // If a video background is provided, upload it
        if (videoBackgroundFile) {
          const videoFormData = new FormData();
          videoFormData.append("video_background", videoBackgroundFile);
          const videoUploadResponse = await fetch(
            `${CONFIG.API_URL}/channel/${response.data._id}/upload-video-background`,
            {
              method: "POST",
              headers: {
                ...getAuthHeaders(),
              },
              body: videoFormData,
            }
          );
          if (!videoUploadResponse.ok) {
            console.error("Failed to upload video background");
          }
        }

        setSuccess(true);
        // Reset form fields and file inputs
        setName("");
        setLanguage("");
        setVoiceId("");
        setChannelId("");
        setChannelType("");
        setGenre(""); // reset genre
        setLogoFile(null);
        setThumbnailFiles([]);
        setVideoBackgroundFile(null);
        setShortBackgroundFile(null);
        setLogoPreview(null);
        setThumbnailPreviews([]);
        setVideoBackgroundPreview(null);
        setShortBackgroundPreview(null);
        setChannelUsername(""); 
      } else {
        setError("Failed to create channel. Please try again.");
      }
    } catch (error) {
      console.error("Error creating channel:", error);
      setError("An error occurred while creating the channel.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && (
        <div className="alert alert-success">
          Channel created successfully!
        </div>
      )}
      <Card>
        <CardHeader>
          <h1>Create a New Channel</h1>
        </CardHeader>
        <CardBody>
          <div className="mb-4">
            <label>Channel Name</label>
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter Channel Name"
            />
          </div>

          <div className="mb-4">
            <label>Language</label>
            <select
              className="form-select"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="">Select a language</option>
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label>Channel Type</label>
            <select
              className="form-select"
              value={channelType}
              onChange={(e) => setChannelType(e.target.value)}
            >
              <option value="">Select channel type</option>
              {channelTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* New: Genre select input */}
          <div className="mb-4">
            <label>Genre</label>
            <select
              className="form-select"
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
            >
              <option value="">Select a genre</option>
              {genres.map((g, index) => (
                <option key={index} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label>Voice ID</label>
            <input
              type="text"
              className="form-control"
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              placeholder="Enter Voice ID"
            />
          </div>

          <div className="mb-4">
            <label>YouTube Channel ID</label>
            <input
              type="text"
              className="form-control"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              placeholder="Enter YouTube Channel ID"
            />
          </div>

          <div className="mb-4">
            <label>Channel Username</label>
            <input
              type="text"
              className="form-control"
              value={channelUsername}
              onChange={(e) => setChannelUsername(e.target.value)}
              placeholder="Enter Channel Username"
            />
          </div>

          <div className="mb-4">
            <label>Upload Logo</label>
            <input
              type="file"
              className="form-control"
              accept="image/*"
              onChange={handleLogoChange}
            />
          </div>
          {logoPreview && (
            <div className="mb-4">
              <label>Logo Preview:</label>
              <div>
                <img
                  src={logoPreview}
                  alt="Logo Preview"
                  style={{ maxWidth: "200px", marginTop: "10px" }}
                />
              </div>
            </div>
          )}

          <div className="mb-4">
            <label>Upload Thumbnails</label>
            <input
              type="file"
              className="form-control"
              accept="image/*"
              multiple
              onChange={handleThumbnailChange}
            />
          </div>
          {thumbnailPreviews.length > 0 && (
            <div className="mb-4">
              <label>Thumbnail Previews:</label>
              <div className="d-flex flex-wrap" style={{ marginTop: "10px" }}>
                {thumbnailPreviews.map((preview, index) => (
                  <img
                    key={index}
                    src={preview}
                    alt={`Thumbnail ${index + 1}`}
                    style={{
                      width: "100px",
                      height: "100px",
                      objectFit: "cover",
                      marginRight: "10px",
                      marginBottom: "10px",
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="mb-4">
            <label>Upload Short Background</label>
            <input
              type="file"
              className="form-control"
              accept="image/*"
              onChange={handleShortBackgroundChange}
            />
          </div>
          {shortBackgroundPreview && (
            <div className="mb-4">
              <label>Short Background Preview:</label>
              <div>
                <img
                  src={shortBackgroundPreview}
                  alt="Short Background Preview"
                  style={{ maxWidth: "200px", marginTop: "10px" }}
                />
              </div>
            </div>
          )}

          <div className="mb-4">
            <label>Upload Video Background</label>
            <input
              type="file"
              className="form-control"
              accept="video/*"
              onChange={handleVideoBackgroundChange}
            />
          </div>
          {videoBackgroundPreview && (
            <div className="mb-4">
              <label>Video Background Preview:</label>
              <div>
                <img src={videoBackgroundPreview} alt="Video Background Preview" style={{ maxWidth: "200px", marginTop: "10px" }} />
              </div>
            </div>
          )}
        </CardBody>
        <CardFooter>
          <button
            onClick={handleCreateChannel}
            className="btn btn-success w-100"
            disabled={isLoading}
          >
            {isLoading ? "Creating..." : "Create Channel"}
          </button>
        </CardFooter>
      </Card>
    </>
  );
};

export default ChannelCreate;
